import React, { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useRouter } from "next/router";

import Button from "@/components/Atoms/Button";
import SVGSprite from "@/components/Atoms/SVGSprite";
import { SuccessModal } from "@/components/Organisms/CallbackModalNew";
import FormInputs from "@/containers/ShyftPassPage/RegisterFormSection/RegistrationForm/FormInputs";

import useRegisterForm from "@/containers/ShyftPassPage/RegisterFormSection/RegistrationForm/useRegisterForm";
import useFormHandler from "@/containers/ShyftPassPage/RegisterFormSection/RegistrationForm/useFormHandler";

import { axios, getURLParams } from "@/utils";

import { FormInputsI } from "@/containers/ShyftPassPage/RegisterFormSection/RegistrationForm/formMap";

import styles from "@/containers/ShyftPassPage/RegisterFormSection/RegistrationForm/FormInputs.module.scss";
import wrapperStyles from "../ShyftPassBookClassRegisterPage.module.scss";

interface RegistrationFormProps extends FormInputsI {
  title: string;
  description?: string;
  urlsArguments: string;
  classId: string;
  paymentSuccessPath: string;
  paymentFailurePath: string;
  isSingleClassPurchasePage?: boolean;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  title,
  description,
  urlsArguments,
  defaultPackageSlug,
  packages,
  classId,
  paymentSuccessPath,
  paymentFailurePath,
  isSingleClassPurchasePage,
  ...rest
}) => {
  const params = getURLParams();
  const _classId = params?.get("class_id") || classId || "";

  const router = useRouter();
  const { slug } = router.query;

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookingError, setBookingError] = useState("");

  const { methods, mapData, selectedPackage, resetData } = useRegisterForm({
    urlsArguments,
    defaultPackageSlug: packages?.[0]?.slug,
    packages,
    classId: _classId,
    isSingleClassPurchasePage,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;

  const { handleBlur, onSubmitCallback } = useFormHandler({
    methods,
    mapData,
    pricing: {
      slug: selectedPackage?.slug,
      price: selectedPackage?.price,
      currency: selectedPackage?.currencyCode,
      package_duration: selectedPackage?.duration,
    },
    setSuccess,
    setIsLoading,
    paymentSuccessPath:
      paymentSuccessPath || "/shyftpass/book-class/payments/success",
    paymentFailurePath:
      paymentFailurePath || "/shyftpass/book-class/payments/failure",
  });

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const errors = document.querySelectorAll(".form-input-error");
      // eslint-disable-next-line no-unused-expressions
      errors?.[0]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [errors]);

  const SuccessModalComp = (
    <div className={styles.successView}>
      <SuccessModal
        showButton={false}
        showAppDownLoadButtonLink
        onHideNutritionCallbackModal={() => {
          resetData();
          setSuccess(false);
        }}
      />
    </div>
  );

  const arrowRight = (
    <SVGSprite
      className="icon"
      icon="icon-arrow-right-book-slot"
      height="24px"
      width="24px"
      style={{ marginLeft: "4px" }}
    />
  );

  const purchasePlanButton = (
    <Button
      isLoading={isLoading}
      className={wrapperStyles.button}
      type="submit"
      disabled={bookingError}
    >
      <span>Pay & book your slot for {selectedPackage?.price}</span>
      {arrowRight}
    </Button>
  );

  const onSubmit = (state) => {
    if (Object.keys(errors)?.length === 0) {
      if (slug) {
        const errorMessage = `Event not available. View <a href="/shyftpass#shyft-pass-workout-schedule" style="color: #0000ee; font-size: inherit; line-height: inherit;">upcoming events</a> for Shyft Pass`;
        axios
          .get(
            `${process.env.NEXT_PUBLIC_LOCAL_API_URL}api/v1/admin/shyft-pass-slug-mapping/${slug}/`
          )
          .then((res) => {
            if (!!res?.data?.center_timings?.find((ct) => ct.id === _classId)) {
              onSubmitCallback(state);
            } else {
              setBookingError(errorMessage);
            }
          })
          .catch(() => {
            setBookingError(errorMessage);
          });
      } else {
        onSubmitCallback(state);
      }
    }
  };

  const FormComp = (
    <div className={[styles.registerForm, "form"].join(" ")} id="buysinglepack">
      <div className={[styles.formHeading, "heading"].join(" ")}>
        <h3 className={[styles.title, "title"].join(" ")}>{title}</h3>
        {description && (
          <p
            className={[styles.description, "description"].join(" ")}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </div>
      <div className={styles.formInputs}>
        <FormProvider {...methods}>
          <FormInputs
            isLoading={isLoading}
            control={control}
            handleSubmit={handleSubmit}
            handleBlur={handleBlur}
            onSubmitCallback={onSubmit}
            purchasePlanButton={purchasePlanButton}
            selectedPackage={selectedPackage}
            bookingError={bookingError}
            isSingleClassPurchasePage={isSingleClassPurchasePage}
            {...rest}
          />
        </FormProvider>
      </div>
    </div>
  );

  return success ? SuccessModalComp : FormComp;
};

export default RegistrationForm;
